/**
 * Manages the rendering of the banners across the site.
 *
 * Historically, the banners tend to appear fixed from the bottom and stack if there are multiple.
 *
 * Expected legacy DOM structure:
 *
 * ```
 *   <div data-banner="<BANNERNAME>">
 *     <div class="banner__wrapper--dismissable">
 *       <div></div>
 *       <a href="#" data-banner-close="<BANNERNAME>"></a>
 *     </div>
 *   </div>
 *
 * Note: Since the introduction of new banners positioned at the top, the DOM structure
 * and event handling may slightly deviate. Eventually, consolidate structure/behavior so
 * there is a single source-of-truth.
 *
 * TODO: Instead of hinting at positioning in the name of function registration below,
 * refactor to pass in configuration option for positioning, i.e. { position: "fixed-top" }.
 *
 * TODO: Refactor to share cookie registration and close handler.
 */

/**
 * Cookie management
 */
const cookieIsSet = (name) => {
  return document.cookie.indexOf(name) > -1;
};

const setBannerClosedCookie = (name) => {
  const date = new Date();
  const midnight = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59).toUTCString();
  let cookieStr = `${name}=closed; expires=${midnight}; path=/;`;
  if (window?.MoMA?.appInfo?.environment === 'production') {
    cookieStr += 'domain=moma.org;';
  }
  document.cookie = cookieStr;
};

/**
 * Banner management
 */
const showBanners = () => {
  const banners = document.querySelectorAll("[data-banner]");

  banners.forEach(banner => {
    setupBanner(banner);
  });
};

const setupBanner = (banner) => {
  if (!banner) return;

  const name = banner.getAttribute("data-banner");

  if (!cookieIsSet(name)) {
    showBanner(banner);
    registerCloseHandler(banner, name);
  }
};

const showBanner = (banner) => {
  banner.classList.remove("banner--loading");
  setTimeout(() => {
    banner.classList.add("banner--active");
  }, 50);
};

const registerCloseHandler = (banner, name) => {
  const closeLink = banner.querySelector("[data-banner-close]");
  if (!closeLink) return;

  closeLink.addEventListener('click', (e) => {
    e.preventDefault();

    banner.classList.remove("banner--active");
    setTimeout(() => {
      banner.classList.add("banner--dismissed");
    }, 200);
    setBannerClosedCookie(name);
  });
};

/**
 * Specific banner initializers
 */

// Top-Positioned Dismissable "Membership Expired/Expiring" Banners
const registerTopMembershipBanner = () => {
  const membershipBanner = document.querySelector('[data-membership-expiration-banner]');
  const membershipBannerWrapper = document.querySelector('[data-membership-expiration-banner-wrapper]');
  const membershipBannerCookieName = 'membershipExpirationBanner';
  const membershipBannerCookieDismissValue = 'dismissed';

  const endOfMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate(), 23, 59, 59).toUTCString();
  };

  const setBannerClosedCookie = (name, value) => {
    const _endOfMonth = endOfMonth();
    document.cookie = `${name}=${value}; expires=${_endOfMonth}; path=/`;
  };

  const hideBanner = (banner) => {
    if (membershipBannerWrapper) {
      membershipBannerWrapper.classList.add("layout/none");
    }

    if (banner) {
      banner.classList.add("layout/none");
      banner.classList.remove("layout/block");
    }
  };

  const registerCloseHandler = (banner, name, dismissValue) => {
    const closeButton = banner.querySelector("[data-membership-expiration-close-button]");
    if (!closeButton) return;

    closeButton.addEventListener('click', (e) => {
      e.preventDefault();

      hideBanner(banner);
      setBannerClosedCookie(name, dismissValue);
    });
  };

  if (membershipBanner) registerCloseHandler(membershipBanner, membershipBannerCookieName, membershipBannerCookieDismissValue);
};

const registerBottomFallbackCookieWarning = () => {
  const fallbackCookieWarning = document.querySelector("[data-banner-tray] [data-cookie-warning]");

  if (fallbackCookieWarning) {
    const fallbackCookieWarningCloseButton = fallbackCookieWarning.querySelector("[data-cookie-warning-close]");

    fallbackCookieWarningCloseButton.addEventListener("click", () => {
      fallbackCookieWarning.style.display = "none";
    });
  }
};

// Bottom-Fixed Visitor Guide Banner
const registerBottomGuideBanner = () => {
  const banner = document.querySelector('[data-guide-banner]');
  if (!banner) return;

  if (document.referrer) {
    const referrerPath = new URL(document.referrer).pathname;
    if (referrerPath === '/visit/guide') {
      const guideLink = banner.querySelector('[data-guide-link]');

      if (guideLink) {
        guideLink.addEventListener('click', (e) => {
          e.preventDefault();
          history.back();
        });
      }
    }
  }

  const bannerCookieName = 'guideBanner';

  const bannerHeight = banner.scrollHeight;
  banner.style.setProperty('--layout--height--max', `${bannerHeight}px`);

  const bannerCloseButton = banner.querySelector('[data-guide-banner-close]');

  if (bannerCloseButton) {
    bannerCloseButton.addEventListener('click', (e) => {
      e.preventDefault();
      banner.style.setProperty('--layout--height--max', '0px');
      setBannerClosedCookie(bannerCookieName);
    });
  }
};

// Bottom-Fixed Onboarding Callout
const registerBottomOnboardingCallout = () => {
  const onboardingPrompt = document.querySelector('[data-onboarding-prompt]');
  const dismissalCookieName = "onboardingPromptDismissed";
  const onboardingCookieDismissValue = 'true';

  const setBannerClosedCookie = (name, value) => {
    const date = new Date();
    date.setDate(date.getDate() + 180);
    document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/`;
  };

  const hideBanner = (banner) => {
    if (banner) {
      banner.classList.add("layout/none");
      banner.classList.remove("layout/block");
    }
  };

  const registerCloseHandler = (banner, name, dismissValue) => {
    const closeButton = banner.querySelector("[data-membership-onboarding-banner-close]");
    if (!closeButton) return;

    closeButton.addEventListener('click', (e) => {
      e.preventDefault();

      hideBanner(banner);
      setBannerClosedCookie(name, dismissValue);
    });
  };

  if (onboardingPrompt) registerCloseHandler(onboardingPrompt, dismissalCookieName, onboardingCookieDismissValue);
};

window.addEventListener("DOMContentLoaded", () => {
  showBanners();

  registerTopMembershipBanner();
  registerBottomGuideBanner();
  registerBottomFallbackCookieWarning();
  registerBottomOnboardingCallout();
});

module.exports = {
  showBanners: showBanners,
  registerBottomFallbackCookieWarning: registerBottomFallbackCookieWarning,
  setBannerClosedCookie: setBannerClosedCookie
};
